import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import QRCodeSVG from 'react-qr-code';
import { weddingApi, GuestNote } from '../services/weddingApi';

interface LocalGuestNote {
  id: number;
  text: string;
  author: string;
  date: string;
  color: string;
}

const LoveNotes: React.FC = () => {
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [guestNotes, setGuestNotes] = useState<GuestNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [newGuestNote, setNewGuestNote] = useState({ text: '', author: '' });
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  // Generate QR code URL
  useEffect(() => {
    const currentUrl = window.location.origin + '/love-notes';
    setQrCodeUrl(currentUrl);
  }, []);

  // Format date for display
  const formatNoteDate = (dateString: string) => {
    const noteDate = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Reset time to compare only dates
    const noteDateOnly = new Date(noteDate.getFullYear(), noteDate.getMonth(), noteDate.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
    
    if (noteDateOnly.getTime() === todayOnly.getTime()) {
      return "Today";
    } else if (noteDateOnly.getTime() === yesterdayOnly.getTime()) {
      return "Yesterday";
    } else {
      return noteDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  // Capitalize first letter of name
  const capitalizeName = (name: string) => {
    if (!name) return name;
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  // Load guest notes from backend
  const loadGuestNotes = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Loading guest notes from backend...');
      const notes = await weddingApi.getGuestNotes();
      console.log('Successfully loaded guest notes:', notes);
      
      // Sort notes by creation date (newest first) before setting state
      const sortedNotes = notes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      console.log('Sorted notes (newest first):', sortedNotes.map((n: any) => ({ text: n.text, author: n.author, createdAt: n.createdAt })));
      
      setGuestNotes(sortedNotes);
      localStorage.setItem('wedding-guest-notes', JSON.stringify(sortedNotes));
    } catch (err) {
      console.error('Failed to load guest notes:', err);
      setError('Backend server is not running. Notes are saved locally. To enable full functionality, start the backend server.');
      
      // Fallback to localStorage
      const localNotes = localStorage.getItem('wedding-guest-notes');
        if (localNotes) {
          try {
            const parsedNotes = JSON.parse(localNotes);
            console.log('Loaded notes from localStorage:', parsedNotes);
            
            // Sort notes by creation date (newest first) before setting state
            const sortedNotes = parsedNotes.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            console.log('Sorted localStorage notes (newest first):', sortedNotes.map((n: any) => ({ text: n.text, author: n.author, createdAt: n.createdAt })));
            
            setGuestNotes(sortedNotes);
          } catch (parseErr) {
            console.error('Failed to parse localStorage notes:', parseErr);
          }
        }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGuestNotes();
  }, []);

  const addGuestNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGuestNote.text.trim() || !newGuestNote.author.trim()) return;

    setSubmitting(true);
    try {
      console.log('Creating new guest note:', newGuestNote);
      const note = await weddingApi.createGuestNote(newGuestNote);
      console.log('Successfully created guest note:', note);
      const updatedNotes = [note, ...guestNotes];
      
      // Sort notes by creation date (newest first) before setting state
      const sortedNotes = updatedNotes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      console.log('Sorted notes after adding new note (newest first):', sortedNotes.map((n: any) => ({ text: n.text, author: n.author, createdAt: n.createdAt })));
      
      setGuestNotes(sortedNotes);
      
      setNewGuestNote({ text: '', author: '' });
      setShowGuestForm(false);
    } catch (err) {
      console.error('Failed to create guest note:', err);
      // Fallback to localStorage
      const localNote: GuestNote = {
        _id: `local_${Date.now()}`,
        id: `local_${Date.now()}`,
        text: newGuestNote.text,
        author: newGuestNote.author,
        color: `bg-${['rose', 'purple', 'blue', 'green', 'yellow', 'pink', 'indigo', 'teal'][Math.floor(Math.random() * 8)]}-400`,
        isApproved: true,
        createdAt: new Date().toISOString()
      };
      
      const updatedNotes = [localNote, ...guestNotes];
      
      // Sort notes by creation date (newest first) before setting state
      const sortedNotes = updatedNotes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      console.log('Sorted localStorage notes after adding new note (newest first):', sortedNotes.map((n: any) => ({ text: n.text, author: n.author, createdAt: n.createdAt })));
      
      setGuestNotes(sortedNotes);
      localStorage.setItem('wedding-guest-notes', JSON.stringify(sortedNotes));
      
      setNewGuestNote({ text: '', author: '' });
      setShowGuestForm(false);
    } finally {
      setSubmitting(false);
    }
  };

  const FloatingHeart: React.FC<{ emoji: string; delay: number; x: string; y: string }> = ({ emoji, delay, x, y }) => {
    return (
      <motion.div
        className="absolute text-4xl cursor-pointer select-none"
        style={{ left: x, top: y }}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ 
          opacity: [0, 0.7, 0],
          scale: [0, 1, 0],
          y: [0, -20, -40]
        }}
        transition={{
          duration: 3,
          delay,
          ease: "easeInOut",
        }}
        whileHover={{ scale: 1.3, opacity: 1, rotate: 0 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => {
          setShowGuestForm(true);
        }}
      >
        {emoji}
      </motion.div>
    );
  };

  const GuestNoteCard: React.FC<{ note: GuestNote; index: number; isLatest: boolean }> = ({ note, index, isLatest }) => {
    const [ref, inView] = useInView({
      threshold: 0.1,
      triggerOnce: true
    });

    // Modern gradient color options
    const gradientOptions = [
      'from-rose-400 via-pink-400 to-rose-500',
      'from-purple-400 via-pink-400 to-purple-500',
      'from-blue-400 via-cyan-400 to-blue-500',
      'from-indigo-400 via-purple-400 to-indigo-500',
      'from-pink-400 via-rose-400 to-pink-500',
      'from-violet-400 via-purple-400 to-violet-500',
    ];
    
    // Generate consistent gradient based on note ID
    const noteId = String(note._id || note.id || note.createdAt || Date.now());
    const seed = noteId.split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
    const gradientIndex = seed % gradientOptions.length;
    const gradient = gradientOptions[gradientIndex];

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, scale: 0.9, rotateY: -10 }}
        animate={inView ? { 
          opacity: 1, 
          scale: 1,
          rotateY: 0
        } : {}}
        transition={{ 
          duration: 0.5,
          delay: index * 0.06,
          ease: "easeOut"
        }}
        className="group perspective-1000"
        whileHover={{ 
          scale: 1.02,
          rotateY: 2,
          transition: { duration: 0.2 }
        }}
      >
        <div className="relative h-full min-h-[200px] max-h-[240px]">
          {/* Main card */}
          <div className={`bg-gradient-to-br ${gradient} rounded-2xl p-4 shadow-2xl h-full flex flex-col relative overflow-hidden transform transition-all duration-300 group-hover:shadow-3xl`}>
            {/* Shine effect overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            {/* Latest indicator - NEW tag on corner */}
            {isLatest && (
              <motion.div
                className="absolute -top-2 -right-2 z-20"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ 
                  scale: 1,
                  rotate: 0
                }}
                transition={{
                  duration: 0.5,
                  delay: 0.2,
                  type: "spring",
                  stiffness: 200
                }}
              >
                <div className="bg-white text-rose-600 font-bold text-xs px-3 py-1.5 rounded-lg shadow-xl flex items-center gap-1.5 border-2 border-rose-200 rotate-12">
                  <motion.span
                    animate={{ 
                      scale: [1, 1.2, 1],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    ✨
                  </motion.span>
                  <span>NEW</span>
                </div>
              </motion.div>
            )}

            {/* Content */}
            <div className="relative z-10 flex flex-col h-full">
              {/* Message text */}
              <div className="flex-1 mb-3 overflow-hidden">
                <p 
                  className="text-white text-lg font-medium leading-relaxed"
                  style={{
                    wordBreak: 'break-word',
                    lineHeight: '1.6',
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    display: '-webkit-box',
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {note.text}
                </p>
              </div>

              {/* Author section */}
              <div className="mt-auto pt-3 border-t border-white/30">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-semibold text-sm mb-0.5 truncate">
                      {capitalizeName(note.author)}
                    </div>
                    <div className="text-white/75 text-xs">
                      {formatNoteDate(note.createdAt)}
                    </div>
                  </div>
                  {/* Heart icon */}
                  <div className="text-white/60 group-hover:text-white/90 transition-colors ml-3 flex-shrink-0">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative corner elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-bl-full transform translate-x-8 -translate-y-8" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-tr-full transform -translate-x-6 translate-y-6" />
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900">
      {/* Hero Section with Interactive Canvas */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/50 to-pink-900/50" />
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-30" />
        </div>

        {/* Floating Hearts */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: 15 }, (_, i) => (
            <FloatingHeart
              key={i}
              emoji={['💕', '💖', '💝', '💗', '💘', '💓'][i % 6]}
              delay={i * 0.5}
              x={`${Math.random() * 100}%`}
              y={`${Math.random() * 100}%`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6">
              Love Notes
            </h1>
            <p className="text-xl md:text-2xl text-purple-200 max-w-3xl mx-auto">
              Sweet messages and well wishes from family and friends
            </p>
          </motion.div>

          {/* Controls */}
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center mb-8">
            <motion.button
              onClick={() => window.location.reload()}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white/20 backdrop-blur-md text-white px-6 py-3 rounded-full font-bold hover:bg-white/30 transition-all duration-300"
            >
              Refresh Notes
            </motion.button>
            
            <motion.button
              onClick={() => setShowGuestForm(!showGuestForm)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-rose-500 to-pink-600 text-white px-6 py-3 rounded-full font-bold hover:shadow-lg transition-all duration-300"
            >
              Add Note 💌
            </motion.button>
          </div>

          {/* QR Code Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-12 flex justify-center"
          >
            <div className=" rounded-3xl p-6 border border-white/20 shadow-2xl">
              <div className="text-center mb-4">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                  Scan the QR code to Leave a Note 📱
                </h3>
                
              </div>
              <div className="flex justify-center p-4 bg-white rounded-2xl">
                {qrCodeUrl && (
                  <QRCodeSVG
                    value={qrCodeUrl}
                    size={250}
                    level="H"
                    fgColor="#000000"
                    bgColor="#ffffff"
                  />
                )}
              </div>
             
            </div>
          </motion.div>

          {/* Guest Notes from Database - Displayed in scattered layout */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-white text-lg">Loading notes...</div>
            </div>
          ) : error ? (
            <div className="bg-red-500/20 backdrop-blur-md rounded-2xl p-6 border border-red-400/30 text-center mb-12">
              <p className="text-red-200">{error}</p>
              <div className="flex gap-4 justify-center mt-4">
                <button
                  onClick={() => window.location.reload()}
                  className="bg-red-500/30 hover:bg-red-500/50 text-white px-4 py-2 rounded-lg transition-all duration-300"
                >
                  Retry
                </button>
                <button
                  onClick={loadGuestNotes}
                  className="bg-blue-500/30 hover:bg-blue-500/50 text-white px-4 py-2 rounded-lg transition-all duration-300"
                >
                  Try Backend Again
                </button>
              </div>
            </div>
          ) : guestNotes.length === 0 ? (
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 text-center mb-12">
              <div className="text-6xl mb-4">💌</div>
              <h3 className="text-xl font-bold text-white mb-2">No guest notes yet</h3>
              <p className="text-purple-200">Be the first to leave a love note for Noor & Priya!</p>
            </div>
          ) : (
            <div className="max-w-7xl mx-auto px-4 mb-12">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {guestNotes
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map((note, index) => (
                    <GuestNoteCard 
                      key={String(note._id || note.id || index)} 
                      note={note} 
                      index={index}
                      isLatest={index === 0}
                    />
                  ))}
              </div>
            </div>
          )}

          {/* Instructions */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-8"
          >
            {/* <p className="text-purple-200 text-sm font-medium">
              Click hearts or the Add Note button to leave a love note
            </p> */}
          </motion.div>
        </div>
      </section>

      {/* Add Guest Note Form */}
      {showGuestForm && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 max-w-2xl w-full"
          >
            <h3 className="text-2xl font-bold text-white mb-6 text-center">Leave a Love Note</h3>
            
            <form onSubmit={addGuestNote} className="space-y-6">
              <div>
                <label className="block text-white font-medium mb-2">Your Name</label>
                <input
                  type="text"
                  value={newGuestNote.author}
                  onChange={(e) => setNewGuestNote({ ...newGuestNote, author: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  placeholder="Enter your name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Your Message</label>
                <textarea
                  value={newGuestNote.text}
                  onChange={(e) => setNewGuestNote({ ...newGuestNote, text: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent h-32 resize-none"
                  placeholder="Share your love and well wishes for Noor & Priya..."
                  required
                />
              </div>
              
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowGuestForm(false)}
                  className="flex-1 bg-white/20 text-white px-6 py-3 rounded-xl font-bold hover:bg-white/30 transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-gradient-to-r from-rose-500 to-pink-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Posting...' : 'Post Note'}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default LoveNotes;